/**
 * The composer — the input half of a session, styled to match the iOS composer
 * (ios-next/Sources/Composer.swift) rather than the bare input it replaced.
 *
 * One rounded input surface: a text field that grows, and beneath it a control
 * row with the model selector on the left and send on the right. Picking a model
 * writes the run's `requestedModel` (RFC 0262), so the worker resolves and
 * records exactly it — the selector is real, not chrome. A failed send says so
 * and restores the text; a message that looks sent but is not is the worst
 * outcome (RFC 0255 §3).
 *
 * The device/placement selector (RFC 0261 slice 3) lands beside the model one
 * when placement can be written; a control that cannot do anything yet is worse
 * than its absence, so it is not stubbed in here.
 *
 * TWO REPRESENTATIONS, ONE INSTANCE (RFC 0266 §3e). When the shell offers a
 * compact slot (agent panel collapsed over an open file) this same component
 * portals itself there and draws tighter. `variant` is presentation only: same
 * draft, same model, same send(). See ComposerSlot.tsx for why it is a portal
 * and not a second component.
 */

import type { AttachmentRef, HarnessPresence } from '@mod/core/model'
import { formatBytes, isImage } from '@mod/core/model'
import {
  COMPOSER_MODELS,
  type ComposerHarnessGroup,
  type ComposerModel,
  harnessIdForOption,
  resolveComposerHarnessGroups,
} from '@mod/core/run'
import {
  ArrowUp,
  Check,
  ChevronDown,
  ChevronRight,
  FolderGit2,
  Plus,
} from 'lucide-react'
import {
  createContext,
  type FocusEvent,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import type { ProjectChoice } from '../../services/sessions/projectChoices'
import type { ComposerVoice } from '../../services/voice/types'
import { useMacSignedIn } from '../../services/world/useMacSignedIn'
import { StatusDot } from '../../shell/window/StatusDot'
import { dragHasFiles, filesFromClipboard } from './attachments'
import { useComposerSlot } from './ComposerSlot'
import { composerAction } from './composerAction'
import { HarnessLogo } from './HarnessLogo'
import {
  activeRefQuery,
  type DraftRef,
  insertRefChip,
  RefPicker,
  reanchorRefs,
  refsToMarkdown,
  splitRefs,
  useComposerRefs,
} from './RefPicker'
import { type PickableSkill, SkillPicker } from './SkillPicker'
import {
  CallControls,
  EndCallButton,
  StartCallButton,
  TalkWhileWorkingButton,
} from './VoiceControls'

const MODEL_KEY = 'composer.model'

/**
 * The model a message is addressed to when nobody has just picked one.
 *
 * Exported because the composer is no longer the only thing that sends: ⌘K can
 * start a session with the typed text as its first message, and that message
 * must go to the SAME model the composer would have used. Reading the same key
 * rather than defaulting separately is the whole point — a first message
 * answered by a different model than every message after it is the kind of
 * inconsistency nobody thinks to report as a bug.
 *
 * When `options` is passed (live harness catalogue), prefer that list; otherwise
 * the static proxy catalogue.
 */
export function preferredComposerModel(options?: ComposerModel[]): {
  provider: string
  model: string
} {
  const chosen = preferredComposerChoice(options)
  return { provider: chosen?.provider ?? '', model: chosen?.model ?? '' }
}

const HARNESSES_KEY = 'composer.harnesses'
/** Fired when the chosen list changes, so a mounted composer applies it now
 *  rather than on its next device change. */
const HARNESSES_EVENT = 'mod:composer-harnesses'

/**
 * The agents the person chose to use (RFC 0337 S6), or null if they never
 * chose. Only the agents Mod drives on a Mac are filtered; Mod's own models and
 * any other group always stay.
 */
export function rememberComposerHarnesses(ids: readonly string[]): void {
  try {
    localStorage.setItem(HARNESSES_KEY, JSON.stringify(ids))
  } catch {
    // best-effort
  }
  window.dispatchEvent(new Event(HARNESSES_EVENT))
}

function chosenHarnesses(): string[] | null {
  try {
    const raw = localStorage.getItem(HARNESSES_KEY)
    const parsed = raw ? (JSON.parse(raw) as unknown) : null
    return Array.isArray(parsed) ? parsed.map(String) : null
  } catch {
    return null
  }
}

/** The agents a person can tick in the first run; anything else is never hidden. */
const CHOOSABLE_HARNESSES = new Set(['claude-code', 'codex', 'cursor'])

/** Drop the choosable agents the person did not tick. No choice keeps all. */
export function filterChosenHarnesses<T extends { id: string }>(
  groups: readonly T[],
  chosen: readonly string[] | null,
): T[] {
  if (!chosen) return [...groups]
  return groups.filter(
    (g) => !CHOOSABLE_HARNESSES.has(g.id) || chosen.includes(g.id),
  )
}

/**
 * Make `harness:model` the composer's choice, as a pick in its menu would.
 * The first run's agent step (RFC 0337 S6) is the other place a person picks.
 */
export function rememberComposerModel(id: string): void {
  try {
    localStorage.setItem(MODEL_KEY, id)
  } catch {
    // best-effort, as in the menu
  }
}

/**
 * A session's own choice. The plain key is only the default a session starts
 * from: picking in one session used to write it and nothing else, so every
 * other session switched with it.
 */
function sessionModelKey(sessionId: string): string {
  return `${MODEL_KEY}:${sessionId}`
}

function storedModelId(sessionId?: string): string {
  try {
    return (
      (sessionId ? localStorage.getItem(sessionModelKey(sessionId)) : null) ??
      localStorage.getItem(MODEL_KEY) ??
      ''
    )
  } catch {
    // Private mode / blocked storage: the default is still a valid answer.
    return ''
  }
}

/** The stored choice itself. Only `provider`/`model` may reach a run — a run's
 *  `requestedModel` is a two-field shape, and putting `id`/`label` on it writes
 *  fields whiffletree never asked for. */
function preferredComposerChoice(
  options?: ComposerModel[],
  sessionId?: string,
): ComposerModel | undefined {
  const catalog = options && options.length > 0 ? options : COMPOSER_MODELS
  const id = storedModelId(sessionId)
  return (
    catalog.find((m) => m.id === id) ??
    reconstructHarnessChoice(id) ??
    catalog[0]
  )
}

/** When presence has not hydrated yet (⌘K), recover a harness:model id from storage. */
function reconstructHarnessChoice(id: string): ComposerModel | undefined {
  const colon = id.indexOf(':')
  if (colon <= 0) return undefined
  const provider = id.slice(0, colon)
  const model = id.slice(colon + 1)
  if (!provider || !model) return undefined
  if (provider === 'openai' || provider === 'anthropic') return undefined
  // Just the model: the harness half of the label is added by whatever renders
  // it, so putting the provider in here reads as "codex · codex · gpt-5".
  return { id, label: model, provider, model }
}

/** One present device the run can be placed on (RFC 0261 slice 3). */
export interface PlacementDevice {
  deviceId: string
  name: string
  /** Harnesses this device can drive (RFC 0269) — feeds the model picker. */
  harnesses?: HarnessPresence[]
}

/** An attachment the composer is holding, before the message is sent.
 *  `previewUrl` is local and immediate: the chip draws from the File itself,
 *  without waiting for the document to sync anywhere. */
export interface PendingAttachment extends AttachmentRef {
  previewUrl?: string
}

export interface ComposerProps {
  /** Append the message, addressed to the chosen model. Rejects to signal a
   *  failed send so the composer can restore the draft. */
  onSend: (
    text: string,
    requestedModel: { provider: string; model: string },
    /** Skills named with `/` (RFC 0283 S5), by skill id. */
    skills?: string[],
    /** RFC 0288: pointers to attachment documents, created before this call. */
    attachments?: AttachmentRef[],
  ) => Promise<void>
  /**
   * Write a file to an attachment document and return its pointer (RFC 0288).
   *
   * A PROP for the same reason `skills` is: creating a document needs a repo,
   * and a composer that reached for one could not mount in a test or in a slot
   * without it. Absent means no attaching, and the `+` button is not drawn.
   */
  onAttach?: (file: File) => Promise<AttachmentRef>
  /** Owner-only project grants offered beside files in the composer `+` menu. */
  projectAttachment?: ReactNode
  /** A run is queued with nothing draining it yet — show "waiting for a worker".
   *  Deliberately not "thinking": a thread that claims to think forever is how a
   *  working system looks broken (RFC 0255 §1). */
  waiting?: boolean
  /** RFC 0301 §3f: a worker has a turn a comment started, and the agent has not
   *  decided yet. Said here rather than in the thread, which it must not touch. */
  reading?: boolean
  /** Live devices the session can run on (RFC 0261 slice 3). Only devices beating
   *  right now — never a dead one, which would just hang the run. */
  devices?: PlacementDevice[]
  /**
   * The skills `/` offers (RFC 0283 S5): the project's, the World's, the
   * installed apps' and Mod's, as `useSkills` resolves them (RFC 0334 S4).
   *
   * A PROP, like `devices`, rather than read here: this component is rendered
   * in tests and in slots with no repo in context, and a hook reaching for one
   * would make a presentational composer refuse to mount without it.
   */
  skills?: PickableSkill[]
  /** Write a new skill named from the `/` query and open it for editing
   *  (RFC 0283 S5). Absent where there is no World or nowhere to open it. */
  onCreateSkill?: (title: string) => void
  /**
   * The agent is working on this session (a run is queued or running).
   *
   * It does NOT gate the send (RFC 0302 S1). A second run sits `queued` and
   * drains when the session is next free, which is what `enqueueRun` has always
   * done for a turn one agent dispatches to another. Refusing the human the same
   * delivery left a thought you have on minute four of a refactor with two
   * destinations: a notes file, or an interrupt.
   *
   * All it does is draw Stop, beside Send rather than over it.
   */
  busy?: boolean
  /** Ask the active run to stop. Cancellation is cooperative — the log flips to
   *  cancelled immediately and the worker abandons its turn shortly after. */
  onStop?: () => Promise<void> | void
  /**
   * Which project this session works in, and the projects it could move to
   * (RFC 0290 S6).
   *
   * WHY HERE AND NOT ON THE CREATE BUTTON. S5 put the choice on the `+` in the
   * cross-project list, which is the one list with no route into it: every
   * other pane is on a project, `backToSessionList` returns a pane to the list
   * it came from, and a new pane is created scoped to the active project. So a
   * person on the desktop app could not reach the picker at all and every
   * create fell back to the window's project. A choice made where the intent is
   * formed, in the box you are already typing in, is reachable by construction.
   *
   * Empty or absent means no choice is offered, which is what a World that has
   * not synced looks like.
   */
  projects?: ProjectChoice[]
  /** The project whose registry currently lists this session. */
  projectId?: string
  /**
   * Move the session to another project. Absent means the control is not drawn
   * even when `projects` is populated, which is how a caller that cannot
   * perform a move (a test, a read-only surface) opts out.
   */
  onSelectProject?: (projectId: string) => void
  /**
   * Whether this session has said anything yet (RFC 0290 S6).
   *
   * The switcher is live only while it has not. A move is a removal and an
   * insertion across two registries, and the daemon resolves a session's
   * project per turn, so moving one mid-run points the turn at a checkout that
   * is not the one it started in. Zero turns is the condition under which there
   * is nothing to point wrong.
   *
   * Read off the transcript rather than stored as a flag: the log already knows
   * whether anyone has spoken, and a second copy of that fact can disagree with
   * it.
   */
  unstarted?: boolean
  /** What the empty field says. The box under the session list and the box in a
   *  thread do different things, and "Message" describes only one of them. */
  placeholder?: string
  /** Take the caret on mount. True in a thread, because opening a session in
   *  order to type is why you opened it. False where the composer is one of
   *  several things on screen. */
  autoFocus?: boolean
  /**
   * The draft, as it is typed (RFC 0284).
   *
   * FOR A HOST THAT SEARCHES WITH THE SAME BOX. The new-pane page is a composer
   * and a search field at once, so it has to see the text in order to rank
   * results against it. REPORTED, not controlled: the draft stays this
   * component's own state, so the send-and-restore path, the `/` menu and the
   * caret bookkeeping keep working exactly as they do in a thread. A host that
   * does not care passes nothing.
   */
  onDraftChange?: (text: string) => void
  /**
   * Let the host take a navigation key. Return true to say it was used.
   *
   * The keys a LIST needs and a text box does not: ↑/↓ to move a selection, Tab
   * to accept it, and Return when the selection is a row rather than the text
   * (RFC 0333 3b). Offered rather than assumed: a host that returns false for
   * Return gets the composer's own send, so Return still sends everywhere a
   * host has not selected something else. Escape stays the composer's, and
   * none of these reach the host while the `/` menu is open, since those keys
   * belong to it first.
   */
  onNavigationKey?: (key: 'up' | 'down' | 'tab' | 'enter') => boolean
  /**
   * What the session has in hand (RFC 0297), drawn above the box.
   *
   * Handed IN rather than drawn beside the composer by the host, because the
   * composer portals itself: a bar beside it stayed in the hidden panel when
   * the composer moved to the compact slot, and the compact box showed nothing
   * it was holding.
   *
   * A FUNCTION when the host draws a line of its own above the box and wants
   * a new session's project and branch ON that line rather than on a second
   * one under it (the compact strip, whose line is the session menu). It is
   * handed the where-controls, or null when there are none to draw, and the
   * composer then draws no row of its own for them.
   */
  holdings?: ReactNode | ((where: ReactNode) => ReactNode)
  /**
   * WHERE this post is going (RFC 0302 S6), drawn above everything else in the
   * box.
   *
   * Only worth saying when it is not obvious, which in practice means a
   * subthread: the pane you are in was opened from a card in another
   * conversation, and the parent is one Back away. Saying it on every thread
   * would be a label under a title that already says the same thing.
   */
  target?: ReactNode
  /**
   * What the session is doing, or what it last said (RFC 0304 3g), drawn
   * between the holdings bar and the box. Handed in for the same reason as
   * `holdings`: the compact box is the frame both belong inside.
   */
  activity?: ReactNode
  /**
   * A voice call on this composer (RFC 0307). Absent means no voice here, and
   * the empty box's button stays Send. Handed in rather than held, because
   * what a call is FOR, its session and tools, is the host's to say.
   */
  voice?: ComposerVoice
  /**
   * The session this box sends to, so its harness and model are its own. A
   * pick here does not move any other session. Absent on a box that starts a
   * session, which reads and writes the default.
   */
  sessionId?: string
}

/**
 * Whether the person is with the compact box right now (RFC 0304 3g).
 *
 * True while they are in it: clicked into it, typing, tabbed in. False once
 * they click or scroll elsewhere on the page. What sits above the box reads
 * this to fold a reply down to one line when attention has moved on, and back
 * out when it returns. Always true outside the compact variant, where the
 * box has a pane of its own and nothing to get out of the way of.
 */
export const ComposerEngaged = createContext(true)

/**
 * Engagement for the compact frame, from three plain signals: the pointer is
 * over it, focus is inside it, and whether the person has clicked or scrolled
 * away since. Hover and focus come from React's own handlers on the frame, so
 * they survive the frame remounting; only "away" needs the document, because
 * a click or a scroll elsewhere never reaches the frame.
 *
 *   engaged = hovered || (focusWithin && !away)
 *
 * So: moving the pointer onto the box unfolds it. Typing in it keeps it. A
 * click or scroll elsewhere folds it even if the caret is still in the box.
 */
function useEngaged(frame: RefObject<HTMLElement | null>, on: boolean) {
  const [hovered, setHovered] = useState(false)
  const [focusWithin, setFocusWithin] = useState(false)
  const [away, setAway] = useState(false)
  useEffect(() => {
    if (!on) return
    const inside = (t: EventTarget | null) =>
      t instanceof Node && frame.current?.contains(t) === true
    const onDown = (e: Event) => setAway(!inside(e.target))
    const onScroll = (e: Event) => {
      if (!inside(e.target)) setAway(true)
    }
    // An app in a pane is an iframe (RFC 0267): a click into it never reaches
    // this document, but it does take focus off this window. Losing the
    // window is losing the person either way, so that alone is "away".
    const onWindowBlur = () => setAway(true)
    document.addEventListener('mousedown', onDown, true)
    document.addEventListener('scroll', onScroll, true)
    window.addEventListener('blur', onWindowBlur)
    return () => {
      document.removeEventListener('mousedown', onDown, true)
      document.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('blur', onWindowBlur)
    }
  }, [frame, on])
  const handlers = {
    onMouseEnter: () => {
      setHovered(true)
      setAway(false)
    },
    onMouseLeave: () => setHovered(false),
    onFocus: () => {
      setFocusWithin(true)
      setAway(false)
    },
    onBlur: (e: FocusEvent) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node | null))
        setFocusWithin(false)
    },
  }
  const engaged = on ? hovered || (focusWithin && !away) : true
  return { engaged, handlers: on ? handlers : {} }
}

export function Composer({
  onSend,
  onAttach,
  projectAttachment,
  waiting,
  reading,
  busy,
  onStop,
  devices = [],
  skills = [],
  onCreateSkill,
  projects = [],
  projectId,
  onSelectProject,
  unstarted = false,
  placeholder = 'Message',
  autoFocus = true,
  onDraftChange,
  onNavigationKey,
  holdings,
  target,
  activity,
  voice: voiceProp,
  sessionId,
}: ComposerProps) {
  // No voice on a Mac that is not signed in to Mod: the call needs Mod's
  // login, and a button that can only fail is worse than none (RFC 0337).
  const voice = useMacSignedIn() === false ? undefined : voiceProp
  const [draft, setDraft] = useState('')
  /** The `@` refs in the draft, shown as their titles until send (RefPicker). */
  const [refs, setRefs] = useState<DraftRef[]>([])
  const refMirrorRef = useRef<HTMLDivElement>(null)
  // Attachments already written to their documents, waiting to be pointed at by
  // the next message. Written BEFORE send because the codec treats an entry's
  // array fields as write-once at append: there is no filling them in later.
  const [pending, setPending] = useState<PendingAttachment[]>([])
  const [attaching, setAttaching] = useState(0)
  const [dragging, setDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const attachmentMenuRef = useRef<HTMLDivElement>(null)
  const [attachmentMenuOpen, setAttachmentMenuOpen] = useState(false)
  useEffect(() => {
    if (!attachmentMenuOpen) return
    const closeOutside = (event: MouseEvent) => {
      if (!attachmentMenuRef.current?.contains(event.target as Node))
        setAttachmentMenuOpen(false)
    }
    const closeEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setAttachmentMenuOpen(false)
    }
    document.addEventListener('mousedown', closeOutside)
    document.addEventListener('keydown', closeEscape)
    return () => {
      document.removeEventListener('mousedown', closeOutside)
      document.removeEventListener('keydown', closeEscape)
    }
  }, [attachmentMenuOpen])
  // Skills named with `/`, in the order chosen. Cleared on send, because naming
  // one is a decision about THIS message: carrying it forward would silently
  // apply a skill to every later message in the session.
  const [named, setNamed] = useState<PickableSkill[]>([])
  const [sending, setSending] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const [focused, setFocused] = useState(false)

  const [chosen, setChosen] = useState(chosenHarnesses)
  useEffect(() => {
    const onChange = () => setChosen(chosenHarnesses())
    window.addEventListener(HARNESSES_EVENT, onChange)
    return () => window.removeEventListener(HARNESSES_EVENT, onChange)
  }, [])
  const harnessGroups = useMemo(
    () => filterChosenHarnesses(resolveComposerHarnessGroups(devices), chosen),
    [devices, chosen],
  )
  const modelOptions = useMemo(
    () => harnessGroups.flatMap((g) => g.models),
    [harnessGroups],
  )
  // The chosen harness:model is an INTENT, kept exactly as stored, and never
  // rewritten by this component. It used to be snapped to the first available
  // option whenever the live catalogue did not contain it — but on mount
  // `devices` is empty for a beat (presence has not synced, the desktop probe
  // has not published), so the catalogue is Mod-only and every chosen CLI
  // harness was silently rewritten to the Mod default before it could appear.
  // That is the "it keeps reverting to Mod" bug: the reset raced hydration.
  const [modelId, setModelId] = useState<string>(
    () =>
      storedModelId(sessionId) ||
      preferredComposerChoice(modelOptions)?.id ||
      '',
  )
  // A session takes the default the first time it is shown, and keeps it: a
  // later pick in another session moves the default, not this one. Re-read
  // when the box is handed a different session without remounting.
  // biome-ignore lint/correctness/useExhaustiveDependencies: sessionId is the trigger; options change on every presence beat
  useEffect(() => {
    // Only a stored choice is pinned, never the catalogue's first entry: before
    // presence arrives that is Mod, and pinning it would undo a CLI default.
    const id = storedModelId(sessionId)
    setModelId(id || preferredComposerChoice(modelOptions)?.id || '')
    if (!sessionId || !id) return
    try {
      if (localStorage.getItem(sessionModelKey(sessionId)) === null)
        localStorage.setItem(sessionModelKey(sessionId), id)
    } catch {
      // best-effort
    }
  }, [sessionId])
  const pickModel = useCallback(
    (id: string) => {
      setModelId(id)
      if (!sessionId) return
      try {
        localStorage.setItem(sessionModelKey(sessionId), id)
      } catch {
        // best-effort
      }
    },
    [sessionId],
  )
  // A harness catalogue we can trust only exists once some device has reported.
  // Until then, show the stored choice rather than a placeholder that would
  // flash Mod and then flip. After that, an intent naming a harness that is not
  // present falls back honestly instead of queueing a run nothing can drain.
  const catalogueKnown = devices.length > 0
  const selected =
    modelOptions.find((m) => m.id === modelId) ??
    (catalogueKnown ? undefined : reconstructHarnessChoice(modelId)) ??
    modelOptions[0]

  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Opening a session puts the caret in the composer, so the thing you almost
  // certainly came to do needs no click. Mount-only: re-focusing on any later
  // render would yank the caret back while you were reading the thread.
  //
  // Not unconditional, because there is now more than one composer on screen.
  // A box that takes the caret because it appeared is a box that interrupts,
  // and two of them would race for it on every render.
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only by design
  useEffect(() => {
    if (autoFocus) textareaRef.current?.focus()
  }, [])

  // Tell a host that searches with this box what is in it. In an effect on
  // `draft` rather than in the textarea's onChange, so the paths that set the
  // draft WITHOUT a keystroke — a send clearing it, a restore putting it back,
  // the `/` menu dismissing itself — report too. Reporting on the keystroke
  // alone left a result list ranked against text the box no longer held.
  useEffect(() => {
    onDraftChange?.(draft)
  }, [draft, onDraftChange])
  // Grow to fit (1–6 lines), like the iOS field's lineLimit(1...6).
  //
  // Keyed on `draft`, not mount. A successful send clears the draft
  // programmatically, and with mount-only deps nothing re-measured — so the box
  // kept the height of the message you just sent and sat there empty and tall
  // until you typed again. Any programmatic change to the draft (send, restore
  // after a failed send) has the same problem, and this covers all of them.
  // `draft` is not read in the body but IS what this measures: scrollHeight is
  // a function of the rendered draft, so the dependency is the trigger rather
  // than an unused value. Dropping it is precisely what caused the bug.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useLayoutEffect(() => {
    const el = textareaRef.current
    if (!el) return
    const fit = () => {
      const height = typedHeight(el)
      // A composer in an inactive space is mounted under `display:none`, where
      // scrollHeight is zero. Freezing that measurement as `0px` made the field
      // stay collapsed after the space became active; its controls still drew,
      // which left a composer with nowhere to type. Keep the intrinsic one-row
      // height until the element is measurable.
      if (height > 0) el.style.height = `${height}px`
    }
    fit()

    // Spaces stay mounted while hidden so their drafts and scroll positions
    // survive a switch. When one becomes visible its textarea's width changes
    // from zero, which is the signal to repeat the measurement. This also
    // refits wrapped drafts when a pane is resized.
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(fit)
    observer.observe(el)
    return () => observer.disconnect()
  }, [draft])

  // RFC 0266 §3e — surviving the move between representations.
  //
  // React keeps this component's state across a portal move, but the DOM nodes
  // are re-created, so the textarea loses focus and selection (and an in-flight
  // IME composition is committed by the removal — it cannot be carried across,
  // which is why this is a snapshot rather than a bare refocus). Keep a running
  // snapshot of caret + focus, and restore it after the variant changes.
  const slot = useComposerSlot()
  // A VARIANT IS A SCALE, AND `element` IS A DESTINATION. They used to be one
  // thing: `compact` meant "portalled", so a host that wanted the tighter
  // floating box without moving the composer anywhere was silently given the
  // thread's scale instead (RFC 0284 S8d). They are separate questions —
  // "how big" and "drawn where" — and the composer strip is the case that
  // proves it: it is already at the bottom of the content area and has nowhere
  // to go, and it is exactly the box `compact` describes.
  const variant = slot.variant
  const caret = useRef({ start: 0, end: 0, focused: false })
  // The caret as STATE as well, because the `@` menu is drawn from it: an
  // `@token` is only being typed while the caret is at its end.
  const [caretAt, setCaretAt] = useState(0)
  const rememberCaret = () => {
    const el = textareaRef.current
    if (!el) return
    caret.current = {
      start: el.selectionStart ?? 0,
      end: el.selectionEnd ?? 0,
      focused: document.activeElement === el,
    }
    setCaretAt(el.selectionEnd ?? 0)
  }
  const prevVariant = useRef(variant)
  useLayoutEffect(() => {
    if (prevVariant.current === variant) return
    prevVariant.current = variant
    const el = textareaRef.current
    const snap = caret.current
    if (!el) return
    // Re-fit the height first: the new node starts at its default row count.
    const height = typedHeight(el)
    if (height > 0) el.style.height = `${height}px`
    if (!snap.focused) return
    el.focus()
    el.setSelectionRange(snap.start, snap.end)
  }, [variant])

  // No `busy` (RFC 0302 S1). A post is accepted whatever the agent is doing; the
  // run queues behind the one in flight.
  const hasDraft = draft.trim().length > 0 || pending.length > 0
  const canSend = hasDraft && !sending

  const [stopping, setStopping] = useState(false)
  const stop = async () => {
    if (!onStop || stopping) return
    setStopping(true)
    try {
      await onStop()
    } catch (err) {
      setFailure(`Could not stop: ${String(err)}`)
    } finally {
      setStopping(false)
    }
  }

  /**
   * The one code path behind all three ways in: the `+` button, a drop, and a
   * paste. Each attaches concurrently, so ten dropped files do not queue behind
   * one another, and each reports its own failure without sinking the rest.
   */
  const addFiles = useCallback(
    async (files: File[]) => {
      if (!onAttach || files.length === 0) return
      setFailure(null)
      setAttaching((n) => n + files.length)
      await Promise.all(
        files.map(async (file) => {
          // The preview is made from the File, not from the stored document, so
          // the chip appears at once and owes nothing to sync.
          const previewUrl = file.type.startsWith('image/')
            ? URL.createObjectURL(file)
            : undefined
          try {
            const ref = await onAttach(file)
            setPending((p) => [...p, { ...ref, previewUrl }])
          } catch (err) {
            if (previewUrl) URL.revokeObjectURL(previewUrl)
            setFailure(String(err instanceof Error ? err.message : err))
          } finally {
            setAttaching((n) => n - 1)
          }
        }),
      )
    },
    [onAttach],
  )

  const removePending = useCallback((docId: string) => {
    setPending((p) => {
      const gone = p.find((a) => a.docId === docId)
      if (gone?.previewUrl) URL.revokeObjectURL(gone.previewUrl)
      return p.filter((a) => a.docId !== docId)
    })
  }, [])

  const send = async () => {
    const shown = draft.trim()
    // What goes out names things by id: each `@title` becomes its link.
    const text = refsToMarkdown(draft, refs).trim()
    // An attachment with no words is a complete message: "look at this" is what
    // the picture is for.
    if ((!text && pending.length === 0) || sending || !selected) return
    setSending(true)
    setFailure(null)
    // Clear optimistically: the message is about to be on the log, and the
    // transcript re-reads either way.
    setDraft('')
    const sent = pending
    // Remove exactly what is going out, rather than clearing wholesale. A file
    // whose attach resolves between here and the state update would otherwise
    // be wiped: its document is written and its pointer thrown away, which
    // reads as an attachment that silently never sent.
    setPending((p) => p.filter((a) => !sent.includes(a)))
    try {
      await onSend(
        text,
        { provider: selected.provider, model: selected.model },
        named.map((n) => n.skillId),
        sent.length > 0 ? sent.map(stripPreview) : undefined,
      )
      setNamed([])
      setRefs([])
      // The thread renders from the documents now, so the local previews are
      // done. Revoking them keeps a long session from leaking a blob per image.
      for (const a of sent) if (a.previewUrl) URL.revokeObjectURL(a.previewUrl)
    } catch (err) {
      // Put the text back rather than losing it — but never over something typed
      // while the send was in flight.
      setDraft((d) => (d.trim().length === 0 ? shown : d))
      // The documents were already written, so the pointers are still good:
      // restoring them means a retry does not re-upload anything.
      setPending((p) => [...sent, ...p])
      setFailure(`Could not send: ${String(err)}`)
    } finally {
      setSending(false)
    }
  }

  /**
   * The `/` menu is open while the draft is a slash command: it starts with a
   * slash.
   *
   * A LEADING slash only, which is what keeps "and/or", a path and a date from
   * opening a menu over them. Spaces do NOT close it, though an earlier version
   * had them do so: a skill is named in words ("release notes"), and a rule
   * that ended the menu at the first space made every multi-word skill
   * unnameable and unwritable from here. Escape dismisses.
   */
  const slashQuery = draft.startsWith('/') ? draft.slice(1) : null

  const pickSkill = useCallback((skill: PickableSkill) => {
    setNamed((n) =>
      n.some((x) => x.skillId === skill.skillId) ? n : [...n, skill],
    )
    // The slash text was the SELECTOR, not the message. Clearing it leaves the
    // composer empty with the skill shown as a chip, so what gets sent is what
    // you then type.
    setDraft('')
    textareaRef.current?.focus()
  }, [])

  /**
   * The `@` menu (see RefPicker): open while the caret ends an `@token`, and
   * never at the same time as `/`, which owns the same keys.
   *
   * Escape closes it for THIS token only, remembered by where its `@` is, so
   * typing on does not reopen what you just dismissed and the next `@` does.
   */
  const refTargets = useComposerRefs()
  const [refDismissed, setRefDismissed] = useState<number | null>(null)
  const refQuery =
    slashQuery === null && refTargets.length > 0 && focused
      ? activeRefQuery(draft, caretAt)
      : null
  const refOpen = refQuery !== null && refQuery.start !== refDismissed
  // Once the token is gone, so is its dismissal: an `@` typed again at the
  // same place is a new one.
  const noRef = refQuery === null
  useEffect(() => {
    if (noRef) setRefDismissed(null)
  }, [noRef])
  const pickRef = useCallback(
    (target: { id: string; title: string }) => {
      const el = textareaRef.current
      if (!el || !refQuery) return
      const next = insertRefChip(draft, refQuery, caretAt, target, refs)
      setDraft(next.text)
      setRefs(next.refs)
      setCaretAt(next.caret)
      // After React has written the value, or the caret lands at the end of
      // the old text instead of after the link.
      requestAnimationFrame(() => {
        el.focus()
        el.setSelectionRange(next.caret, next.caret)
      })
    },
    [draft, refQuery, caretAt, refs],
  )

  const compact = variant === 'compact'
  // The composer as the subject of a page, not a bar at the foot of one. It
  // fills the column it is given rather than centring in a 3xl one, because on
  // that page it IS the column.
  const page = variant === 'page'

  /**
   * Past one line, the text takes a row of its own at the full width of the
   * box and the controls drop under it.
   *
   * In the one-row box the text sits between attach and the model, which is
   * right for a sentence and cramped for a paragraph: a long draft wrapped in
   * the narrow middle and grew into a tall thin column.
   *
   * It stays wide until the draft is cleared or cut back below where it
   * wrapped. Dropping back the moment the text fits the WIDE row would put it
   * in the narrow one, where it wraps again, and the box would flip on every
   * keystroke.
   */
  const [wide, setWide] = useState(false)
  const wideFrom = useRef(0)
  // biome-ignore lint/correctness/useExhaustiveDependencies: `draft` is the trigger; what it measures is the rendered text
  useLayoutEffect(() => {
    const el = textareaRef.current
    if (!el || page) return
    const check = () => {
      const length = el.value.length
      // `leading-6` is 24px a line; anything past half a second line wraps.
      // What is TYPED wraps, never the placeholder: an empty box is one line.
      const wraps = length > 0 && el.scrollHeight > 36
      if (wraps) {
        wideFrom.current = Math.min(wideFrom.current || length, length)
        setWide(true)
      } else if (length === 0 || length < wideFrom.current) {
        wideFrom.current = 0
        setWide(false)
      }
    }
    check()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(check)
    observer.observe(el)
    return () => observer.disconnect()
  }, [draft, page])

  const frameRef = useRef<HTMLDivElement>(null)
  const { engaged, handlers: engage } = useEngaged(frameRef, compact)

  // The pieces of the box, laid out below as one row (RFC 0277 S14), or, on
  // the new-pane page, as the larger two-row box it was.
  const textSize = page
    ? 'max-h-56 min-h-24 text-[19px] leading-7'
    : 'max-h-42 text-sm leading-6'
  const refRuns = refs.length > 0 ? splitRefs(draft, refs) : null
  const hasRefRun = refRuns?.some((r) => r.ref) ?? false
  const textareaBox = (
    <textarea
      ref={textareaRef}
      value={draft}
      onPaste={(e) => {
        if (!onAttach) return
        const files = filesFromClipboard(e.clipboardData)
        if (files.length === 0) return
        // Only when the clipboard actually carried files: pasting text
        // must still paste text, and a copied image from a web page
        // arrives as both, where the file is the one we want.
        e.preventDefault()
        void addFiles(files)
      }}
      onScroll={(e) => {
        if (refMirrorRef.current)
          refMirrorRef.current.scrollTop = e.currentTarget.scrollTop
      }}
      onChange={(e) => {
        setDraft(e.target.value)
        const next = e.target.value
        setRefs((r) => reanchorRefs(draft, next, r))
        setCaretAt(e.target.selectionEnd ?? 0)
        const el = e.currentTarget
        el.style.height = `${typedHeight(el)}px`
      }}
      onFocus={() => {
        setFocused(true)
        rememberCaret()
      }}
      onBlur={() => {
        setFocused(false)
        caret.current = { ...caret.current, focused: false }
      }}
      onSelect={rememberCaret}
      onKeyUp={rememberCaret}
      onClick={rememberCaret}
      onKeyDown={(e) => {
        // The host's keys first, but never while `/` is open: the skill
        // menu owns the arrows for as long as it is on screen.
        if (onNavigationKey && slashQuery === null && !refOpen) {
          const nav =
            e.key === 'ArrowUp'
              ? 'up'
              : e.key === 'ArrowDown'
                ? 'down'
                : e.key === 'Tab' && !e.shiftKey
                  ? 'tab'
                  : e.key === 'Enter' &&
                      !e.shiftKey &&
                      !e.nativeEvent.isComposing
                    ? 'enter'
                    : null
          // Only when the host says it USED the key. An unconsumed arrow
          // must still move the caret, and an unconsumed Tab must still
          // move focus out of the box.
          if (nav && onNavigationKey(nav)) {
            e.preventDefault()
            return
          }
        }
        // Not while an IME is composing: that Enter commits the word, and
        // sending then posts half of it.
        if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
          e.preventDefault()
          void send()
        }
        // Rung 2 of the Esc ladder (RFC 0266 §3d): a composer with text
        // blurs and KEEPS the draft, and stops here so the shell does not
        // also close the file. Empty, it falls through and Esc closes.
        if (e.key === 'Escape' && draft.length > 0) {
          e.preventDefault()
          e.stopPropagation()
          e.currentTarget.blur()
        }
      }}
      rows={1}
      placeholder={placeholder}
      disabled={sending}
      // `block`: an inline textarea sits on the line's baseline and
      // leaves descender space under itself, which read as extra
      // padding below the text in the one-line compact box.
      className={`block w-full resize-none bg-transparent outline-none placeholder:truncate placeholder:text-muted-foreground ${
        hasRefRun ? 'text-transparent caret-foreground' : 'text-foreground'
      } ${textSize}`}
    />
  )
  // A ref reads as its title in blue, as it does in a comment. The textarea
  // cannot colour part of its text, so the text is drawn again underneath,
  // character for character, and the textarea's own goes transparent.
  // Always wrapped, so the textarea is never remounted (and never loses the
  // caret) when the first ref arrives or the last one goes.
  const textareaEl = (
    <div className="relative">
      {hasRefRun && refRuns ? (
        <div
          ref={refMirrorRef}
          aria-hidden
          className={`pointer-events-none absolute inset-0 overflow-hidden whitespace-pre-wrap break-words text-foreground ${textSize}`}
        >
          {refRuns.map((run) =>
            run.ref ? (
              <span
                key={run.at}
                className="rounded-sm bg-blue-500/15 text-blue-400"
              >
                {run.text}
              </span>
            ) : (
              <span key={run.at}>{run.text}</span>
            ),
          )}
          {/* A trailing newline needs a character after it to take a line. */}
          {'\u200b'}
        </div>
      ) : null}
      {textareaBox}
    </div>
  )
  const attachEl = (
    <>
      {(onAttach || projectAttachment) && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              void addFiles(Array.from(e.target.files ?? []))
              // Reset, or picking the same file twice in a row is a no-op
              // because the value has not changed.
              e.target.value = ''
            }}
          />
          <div ref={attachmentMenuRef} className="relative">
            <button
              type="button"
              onClick={() => setAttachmentMenuOpen((open) => !open)}
              title="Add to session"
              aria-label="Add to session"
              aria-expanded={attachmentMenuOpen}
              className="flex size-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <Plus className="size-4" aria-hidden />
            </button>
            {attachmentMenuOpen && (
              <div
                role="dialog"
                aria-label="Add to session"
                className="absolute bottom-full left-0 z-50 mb-2 w-72 space-y-2 rounded-lg border border-border bg-background p-2 text-sm shadow-lg"
              >
                {onAttach && (
                  <button
                    type="button"
                    onClick={() => {
                      setAttachmentMenuOpen(false)
                      fileInputRef.current?.click()
                    }}
                    className="w-full rounded px-2 py-1 text-left hover:bg-muted"
                  >
                    Attach files
                  </button>
                )}
                {projectAttachment}
              </div>
            )}
          </div>
        </>
      )}
    </>
  )
  const modelEl = (
    <HarnessModelMenu
      // The RESOLVED choice, not the raw intent: the trigger must name
      // the harness the next message will actually go to.
      selected={selected}
      groups={harnessGroups}
      onPick={pickModel}
    />
  )
  const projectEl = (
    <>
      {/* Which project this work lands in (RFC 0290 S6). Only while the
          session is unstarted and only when there is more than one place
          it could go: a capsule offering a single choice is furniture. */}
      {unstarted && onSelectProject && projectId && projects.length > 1 ? (
        <ProjectMenu
          projects={projects}
          projectId={projectId}
          unstarted={unstarted}
          onSelect={onSelectProject}
        />
      ) : null}
    </>
  )
  // Where a new session will work: its home project, and nothing about git
  // (RFC 0362 3a). Null once the session exists, which says it in its header.
  const whereEl = unstarted && projectId ? projectEl : null
  const whereOnHostLine = typeof holdings === 'function'
  const holdingsEl =
    typeof holdings === 'function' ? holdings(whereEl) : holdings
  const onCall = voice?.status === 'connecting' || voice?.status === 'live'
  const action = composerAction({
    hasDraft,
    sending,
    canStopRun: Boolean(busy && onStop),
    hasVoice: Boolean(voice),
    // A failed call still holds the button until it is dismissed: its error
    // is on screen, and a Stop there that ended the agent's turn instead was
    // the one press that did something else.
    onCall: onCall || voice?.status === 'error',
  })
  const actionEl = (
    <>
      {/* One stable action position. While work is running an empty
          composer means Stop; typing changes that same affordance to
          Send. Clearing the draft restores Stop without moving either
          action under the pointer. With voice, an idle empty box starts a
          call, and on a call the empty box's Stop ends it (RFC 0307). */}
      {action === 'stop-run' ? (
        <>
          {/* Talking WHILE it works is the point of a call (RFC 0307), so
              Stop keeps the main position and the call sits beside it. */}
          {voice ? <TalkWhileWorkingButton onClick={voice.start} /> : null}
          <StopButton stopping={stopping} onClick={stop} />
        </>
      ) : action === 'end-call' && voice ? (
        <EndCallButton onClick={voice.stop} />
      ) : action === 'start-call' && voice ? (
        <StartCallButton connecting={false} onClick={voice.start} />
      ) : (
        <SendButton sending={sending} enabled={canSend} onClick={send} />
      )}
    </>
  )
  const callEl = onCall && voice ? <CallControls voice={voice} /> : null
  // On a call the controls take the row, so the text gets its own (the same
  // wrap a long draft gets).
  const rowWide = wide || callEl !== null

  const body = (
    <div className={compact ? 'px-3 pb-3' : page ? '' : 'px-3 pt-2 pb-1.5'}>
      <div
        ref={frameRef}
        {...engage}
        className={`group mx-auto w-full ${
          compact
            ? 'max-w-[600px] rounded-3xl border border-border bg-background shadow-lg'
            : page
              ? ''
              : 'max-w-3xl'
        }`}
      >
        {target ? (
          <div
            className={`above empty:hidden text-[0.72rem] text-muted-foreground ${compact ? 'px-3 pt-2' : 'pb-1'}`}
          >
            {target}
          </div>
        ) : null}
        {holdingsEl ? (
          // `empty:hidden`: the bar draws nothing when nothing is held, and the
          // compact box should not keep its padding for a bar that is not there.
          <div className={`above empty:hidden ${compact ? 'px-3 pt-2' : ''}`}>
            {holdingsEl}
          </div>
        ) : null}
        {activity ? (
          <div className={`above empty:hidden ${compact ? 'px-3 pt-2' : ''}`}>
            <ComposerEngaged.Provider value={engaged}>
              {activity}
            </ComposerEngaged.Provider>
          </div>
        ) : null}
        {failure || (voice?.status === 'error' && voice.error) ? (
          <div className="mb-2 flex items-center gap-1.5 px-1 text-red-500 text-xs">
            <span aria-hidden>⚠</span>
            <span>{failure ?? voice?.error}</span>
          </div>
        ) : waiting ? (
          <WaitingIndicator />
        ) : reading ? (
          <ReadingIndicator />
        ) : null}

        {/* A new session says where it will work above the box, as Cursor
            does: the project, then the checkout (RFC 0277 S14). A session
            that exists says it in its header instead. */}
        {whereEl && !whereOnHostLine ? (
          <div
            className={`flex min-w-0 items-center gap-0.5 pb-1 ${compact ? 'px-3 pt-2' : ''}`}
          >
            {whereEl}
          </div>
        ) : null}
        {/* biome-ignore lint/a11y/noStaticElementInteractions: a drop target is
            pointer-only by nature, and there is nothing to give it a role as:
            it is the composer box, not a control. Attaching by keyboard goes
            through the + button, which is a real button and does the same
            thing, so the capability is not gated on being able to drag. */}
        <div
          onDragOver={(e) => {
            if (!onAttach || !dragHasFiles(e.dataTransfer)) return
            // Both are required, and in this order: without preventDefault the
            // browser navigates to the dropped file instead of giving it to us.
            e.preventDefault()
            e.dataTransfer.dropEffect = 'copy'
            setDragging(true)
          }}
          onDragLeave={(e) => {
            // Ignore the leave events fired crossing between children, which
            // would otherwise flicker the highlight on every internal boundary.
            if (e.currentTarget.contains(e.relatedTarget as Node | null)) return
            setDragging(false)
          }}
          onDrop={(e) => {
            if (!onAttach || !dragHasFiles(e.dataTransfer)) return
            e.preventDefault()
            setDragging(false)
            void addFiles(Array.from(e.dataTransfer.files))
          }}
          // Compact only: when a bar is drawn above (`.above` holding a
          // rendered one) the box steps in from the frame's edge, so it reads
          // as sitting inside the same container as the bars rather than as
          // the frame itself.
          className={`relative border bg-muted/40 transition-[margin,border-radius,border-color,background-color] ${
            page ? 'rounded-[15px] px-5 pt-5 pb-4' : 'rounded-3xl px-2 py-1.5'
          } ${
            compact
              ? 'group-has-[.above:not(:empty)]:mx-2 group-has-[.above:not(:empty)]:mb-2 group-has-[.above:not(:empty)]:rounded-2xl'
              : ''
          } ${
            dragging
              ? 'border-foreground/40 bg-muted/70'
              : focused
                ? 'border-foreground/20'
                : 'border-foreground/[0.08]'
          }`}
        >
          {refOpen && refQuery ? (
            <RefPicker
              targets={refTargets}
              query={refQuery.query}
              onPick={pickRef}
              onDismiss={() => setRefDismissed(refQuery.start)}
            />
          ) : null}
          {slashQuery !== null && (
            <SkillPicker
              skills={skills}
              query={slashQuery}
              onPick={pickSkill}
              onCreate={
                onCreateSkill &&
                ((title) => {
                  setDraft('')
                  onCreateSkill(title)
                })
              }
              onDismiss={() => setDraft('')}
            />
          )}

          {/* What this message will run with. A chip rather than text in the
              box, because the skill is not part of what you are saying. */}
          {named.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-1">
              {named.map((skill) => (
                <button
                  key={skill.skillId}
                  type="button"
                  onClick={() =>
                    setNamed((n) =>
                      n.filter((x) => x.skillId !== skill.skillId),
                    )
                  }
                  title="Remove"
                  className="rounded-full bg-muted px-2 py-0.5 text-muted-foreground text-xs hover:text-foreground"
                >
                  /{skill.title} ×
                </button>
              ))}
            </div>
          )}

          {/* What is going WITH the message. Images show themselves; anything
              else is named, because a generic icon says less than a filename. */}
          {(pending.length > 0 || attaching > 0) && (
            <div className="mb-2 flex flex-wrap items-center gap-2">
              {pending.map((a) => (
                <PendingChip
                  key={a.docId}
                  attachment={a}
                  onRemove={() => removePending(a.docId)}
                />
              ))}
              {attaching > 0 && (
                <span className="text-muted-foreground text-xs">
                  Attaching {attaching}…
                </span>
              )}
            </div>
          )}

          {page ? (
            <>
              {textareaEl}
              <div className="mt-2 flex items-center gap-2">
                {attachEl}
                {callEl}
                {modelEl}
                <div className="flex-1" />
                {actionEl}
              </div>
            </>
          ) : (
            // One row: attach, the message, the agent and model, send. The
            // holdings and replies above keep the room a second row took.
            //
            // Past one line the message moves up to a row of its own (`wide`).
            // By CSS order, not by moving it in the tree: a textarea moved to
            // another parent is a new node, and loses the caret mid-word.
            <div className="flex flex-wrap items-end gap-1.5">
              {attachEl}
              {callEl}
              <div
                className={
                  rowWide
                    ? 'order-first w-full basis-full px-1 pt-1'
                    : 'min-w-0 flex-1 self-center'
                }
              >
                {textareaEl}
              </div>
              <div className={rowWide ? 'flex-1' : 'hidden'} />
              {modelEl}
              {actionEl}
            </div>
          )}
        </div>
      </div>
    </div>
  )

  // Same element, drawn somewhere else. The instance never unmounts, so the
  // draft, the model, and an in-flight send all survive the move.
  //
  // On `element` ALONE, not on the variant: a host that offers a destination
  // wants the composer there whatever scale it asked for, and a host that
  // offers none draws it in place. Gating this on `compact` is what made the
  // scale and the destination one decision.
  return slot.element ? createPortal(body, slot.element) : body
}

/** Drop the local-only preview before the pointer goes on the log. */
function stripPreview(a: PendingAttachment): AttachmentRef {
  const { previewUrl: _previewUrl, ...ref } = a
  return ref
}

/**
 * One attached file, before send. An image shows itself; anything else is a
 * named chip, since a generic document icon tells you less than "notes.pdf".
 */
function PendingChip({
  attachment,
  onRemove,
}: {
  attachment: PendingAttachment
  onRemove: () => void
}) {
  const image = isImage(attachment.mimeType) && attachment.previewUrl
  return (
    <span className="group relative inline-flex">
      {image ? (
        <img
          src={attachment.previewUrl}
          alt={attachment.name}
          className="size-14 rounded-lg border border-border object-cover"
        />
      ) : (
        <span className="flex max-w-50 items-center gap-1.5 rounded-lg border border-border bg-muted/60 px-2 py-1.5">
          <span className="truncate text-foreground text-xs">
            {attachment.name}
          </span>
          <span className="shrink-0 text-muted-foreground text-[10px]">
            {formatBytes(attachment.bytes)}
          </span>
        </span>
      )}
      <button
        type="button"
        onClick={onRemove}
        title={`Remove ${attachment.name}`}
        aria-label={`Remove ${attachment.name}`}
        className="-right-1.5 -top-1.5 absolute flex size-4.5 items-center justify-center rounded-full border border-border bg-background text-muted-foreground text-xs opacity-0 transition-opacity group-hover:opacity-100 hover:text-foreground focus-visible:opacity-100"
      >
        ×
      </button>
    </span>
  )
}

/**
 * Two-pane harness → model picker, styled like AppsModal: hairline icon tiles,
 * 12px type, no cards, quiet hover. Left lists harnesses (Mod + connected
 * CLIs); right lists that harness’s models.
 */
/** Room the harness menu needs; above that, it opens up as it always has. */
const MENU_HEIGHT = 380

/** Whether the harness menu opens below its button rather than above. */
export function opensBelow(spaceAbove: number, spaceBelow: number): boolean {
  return spaceAbove < MENU_HEIGHT && spaceBelow > spaceAbove
}

function HarnessModelMenu({
  selected,
  groups,
  onPick,
}: {
  /** The resolved choice, which may name a harness not in `groups` yet while
   *  presence hydrates — the trigger still labels it, so nothing flashes Mod. */
  selected: ComposerModel | undefined
  groups: ComposerHarnessGroup[]
  onPick: (id: string) => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  /** Opens below when there is no room above: on the new-pane page the
   *  composer sits near the top, and the menu ran off the window. */
  const [opensDown, setOpensDown] = useState(false)
  const toggle = () => {
    const box = ref.current?.getBoundingClientRect()
    if (box) setOpensDown(opensBelow(box.top, window.innerHeight - box.bottom))
    setOpen((o) => !o)
  }
  const [focusHarness, setFocusHarness] = useState(() =>
    harnessIdForOption(selected, groups),
  )

  useEffect(() => {
    if (!open) return
    setFocusHarness(harnessIdForOption(selected, groups))
  }, [open, selected, groups])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const activeGroup =
    groups.find((g) => g.id === focusHarness) ?? groups[0] ?? null

  const pick = (id: string) => {
    onPick(id)
    try {
      localStorage.setItem(MODEL_KEY, id)
    } catch {
      // best-effort
    }
    setOpen(false)
  }

  // The agent is its logo; the words are the model, which is what changes
  // most (RFC 0277 S14). The full name is the tooltip.
  const triggerTitle = selected
    ? selected.provider === 'openai' || selected.provider === 'anthropic'
      ? `Mod · ${selected.label}`
      : `${groups.find((g) => g.id === selected.provider)?.label ?? selected.provider} · ${selected.label}`
    : 'Model'
  const triggerLabel = selected ? selected.label : 'Model'

  const triggerHarnessId = harnessIdForOption(selected, groups)

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={toggle}
        title={triggerTitle}
        aria-label={triggerTitle}
        className="flex items-center gap-1.5 rounded-full px-2 py-1.5 font-medium text-muted-foreground text-xs hover:bg-foreground/10"
      >
        <HarnessLogo
          harnessId={triggerHarnessId}
          // `!`: the logo sets its own w-5 h-5 and `cn` only joins classes,
          // so a plain h-3 w-3 loses to them in the stylesheet's order.
          className="h-3! w-3! shrink-0 text-foreground"
        />
        <span className="max-w-[10rem] truncate">{triggerLabel}</span>
        <ChevronDown className="size-3" strokeWidth={2.5} />
      </button>
      {open && activeGroup ? (
        <div
          role="dialog"
          aria-label="Choose harness and model"
          className={`absolute z-20 ${opensDown ? 'top-full left-0 mt-1.5' : 'right-0 bottom-full mb-1.5'} w-[min(28rem,calc(100vw-2rem))] overflow-hidden rounded-[var(--radius-project)] border border-border bg-background shadow-lg`}
        >
          {/*
            Height is driven by the harness column only. Models are absolutely
            positioned beside it so a long model list scrolls instead of
            stretching empty space under the harnesses.
          */}
          <div className="relative">
            <div className="w-[11.5rem] border-border/60 border-r py-2">
              <p className="mb-1.5 px-3 font-semibold text-foreground text-xs leading-tight">
                Harness
              </p>
              <div className="flex flex-col">
                {groups.map((g) => {
                  const active = g.id === activeGroup.id
                  return (
                    <button
                      key={g.id}
                      type="button"
                      onMouseEnter={() => setFocusHarness(g.id)}
                      onFocus={() => setFocusHarness(g.id)}
                      onClick={() => setFocusHarness(g.id)}
                      className={`group/h -mx-0 flex w-full items-start gap-2 px-3 py-2 text-left transition-colors ${
                        active ? 'bg-muted' : 'hover:bg-muted/70'
                      }`}
                    >
                      <span className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-background text-foreground">
                        <HarnessLogo harnessId={g.id} className="size-3.5" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-1 font-semibold text-foreground text-xs leading-tight">
                          <span className="truncate">{g.label}</span>
                          <ChevronRight
                            size={12}
                            className={`shrink-0 text-muted-foreground/60 ${active ? 'opacity-100' : 'opacity-0 group-hover/h:opacity-100'}`}
                          />
                        </span>
                        <span className="mt-0.5 block truncate text-muted-foreground text-xs leading-normal">
                          {g.detail}
                        </span>
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
            <div className="absolute inset-y-0 left-[11.5rem] right-0 flex flex-col py-2">
              <p className="mb-1.5 shrink-0 px-3 font-semibold text-foreground text-xs leading-tight">
                Model
              </p>
              <div className="min-h-0 flex-1 overflow-y-auto">
                {activeGroup.models.map((m) => {
                  const isSel = m.id === selected?.id
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => pick(m.id)}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-foreground text-xs transition-colors hover:bg-muted"
                    >
                      <Check
                        className={`size-3.5 shrink-0 ${isSel ? 'opacity-100' : 'opacity-0'}`}
                        strokeWidth={2.5}
                      />
                      <span className="truncate font-medium">{m.label}</span>
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

/**
 * Which project this session works in (RFC 0290 S6).
 *
 * A capsule like the placement menu, and deliberately the same shape: both
 * answer "where does this go" and a person should not have to learn two
 * controls for one question asked twice.
 *
 * TWO STATES, AND THE READ-ONLY ONE IS THE POINT. Once a turn exists the
 * capsule stays on screen and stops being a button. It has to keep saying where
 * the work is going, because that is the fact a person needs most once they can
 * no longer change it, and a control that vanishes on its first use leaves them
 * with no way to check what they chose.
 *
 * An unavailable project is SHOWN and refused, never hidden. Hiding it leaves a
 * person wondering where their project went; the reason names the fix, which is
 * to point a machine at the directory.
 */
function ProjectMenu({
  projects,
  projectId,
  unstarted,
  onSelect,
}: {
  projects: ProjectChoice[]
  projectId: string
  unstarted: boolean
  onSelect: (projectId: string) => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const current = projects.find((p) => p.projectId === projectId)
  // The id, shortened, when the World has not named it yet. Never "unknown":
  // the session IS somewhere, and saying otherwise is worse than saying less.
  const label = current?.name ?? `${projectId.slice(0, 8)}…`

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  // Closes itself the moment the session starts, so a menu left open across the
  // first send does not sit there offering a move that would now be refused.
  useEffect(() => {
    if (!unstarted) setOpen(false)
  }, [unstarted])

  if (!unstarted)
    return (
      <span
        className="flex items-center gap-1 rounded-full bg-foreground/[0.06] px-2.5 py-1.5 font-medium text-muted-foreground text-xs"
        title="Where this session works. Fixed by its first message."
      >
        <FolderGit2 className="size-3" strokeWidth={2.25} />
        <span>{label}</span>
      </span>
    )

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Where this session works. Changeable until the first message."
        className="flex items-center gap-1 rounded-md px-1.5 py-1 text-muted-foreground text-xs hover:bg-muted hover:text-foreground"
      >
        <FolderGit2 className="size-3" strokeWidth={2.25} />
        <span>{label}</span>
        <ChevronDown className="size-3" strokeWidth={2.5} />
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute bottom-full left-0 z-10 mb-1.5 min-w-52 overflow-hidden rounded-xl border border-border bg-background py-1 shadow-lg"
        >
          {projects.map((p) => (
            <button
              key={p.projectId}
              type="button"
              role="menuitem"
              disabled={p.disabled}
              title={p.reason}
              onClick={() => {
                // The list is a render old by the time it is clicked, and the
                // cost of accepting an unservable choice is a session nobody
                // answers. `disabled` stops a mouse; this stops everything else.
                if (p.disabled) return
                setOpen(false)
                if (p.projectId !== projectId) onSelect(p.projectId)
              }}
              className={`flex w-full flex-col items-start gap-0.5 px-3 py-1.5 text-left ${
                p.disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-muted'
              }`}
            >
              <span className="flex w-full items-center gap-1.5">
                <span className="flex-1 truncate text-sm">{p.name}</span>
                {p.projectId === projectId ? (
                  <Check className="size-3 shrink-0" strokeWidth={2.5} />
                ) : null}
              </span>
              <span className="w-full truncate text-[11px] text-muted-foreground">
                {p.reason}
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}

/** The send affordance. Fills with accent once there is something to send, and
 *  shrinks on press for direct, physical feedback on the most-used action. */
function SendButton({
  sending,
  enabled,
  onClick,
}: {
  sending: boolean
  enabled: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!enabled}
      className={`flex size-8 items-center justify-center rounded-full transition-all active:scale-90 ${
        enabled || sending
          ? 'bg-foreground text-background'
          : 'bg-foreground/[0.08] text-muted-foreground'
      }`}
      aria-label="Send"
    >
      {sending ? (
        <span className="size-3.5 animate-spin rounded-full border-2 border-background/40 border-t-background" />
      ) : (
        <ArrowUp className="size-4" strokeWidth={2.5} />
      )}
    </button>
  )
}

/** Occupies the composer's one action position while work runs and no draft is
 * waiting. Typing replaces it with Send; clearing the draft restores it. */
function StopButton({
  stopping,
  onClick,
}: {
  stopping: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={stopping}
      className="flex size-8 items-center justify-center rounded-full bg-foreground/[0.12] text-foreground transition-all active:scale-90 hover:bg-foreground/[0.18]"
      aria-label="Stop"
      title="Stop the agent"
    >
      {stopping ? (
        <span className="size-3.5 animate-spin rounded-full border-2 border-foreground/30 border-t-foreground" />
      ) : (
        <span className="size-2.5 rounded-[2px] bg-current" />
      )}
    </button>
  )
}

/** Shown while the agent reads what a signal brought, before it decides
 *  (RFC 0301 §3f). Grey, not orange: nothing is wrong. */
function ReadingIndicator() {
  return (
    <div className="mb-2 flex items-center gap-1.5 px-1 text-muted-foreground text-xs">
      <StatusDot status="reading" />
      <span>reading</span>
    </div>
  )
}

/** Shown while a run is queued with nothing draining it (RFC 0255 §1). */
function WaitingIndicator() {
  return (
    <div className="mb-2 flex items-center gap-1.5 px-1 text-muted-foreground text-xs">
      <StatusDot status="queued" />
      <span>waiting for a worker</span>
    </div>
  )
}

/**
 * The box's height for what is TYPED, never for the placeholder (fix: the
 * flickering composer).
 *
 * An empty textarea's scrollHeight includes its placeholder, so a placeholder
 * just longer than one line ("Message Worktree instances: test branches side
 * by side") measured two rows. The box grew, the composer went to its wide
 * layout, the placeholder fit on one line there, the box shrank and went back,
 * and everything above it jumped with every turn of that loop. The placeholder
 * is lifted for the measurement only, inside one synchronous call, so it never
 * paints without it. Capped at seven rows, as it always was.
 */
export function typedHeight(el: HTMLTextAreaElement, max = 168): number {
  el.style.height = 'auto'
  if (el.value !== '') return Math.min(el.scrollHeight, max)
  const placeholder = el.placeholder
  el.placeholder = ''
  const height = Math.min(el.scrollHeight, max)
  el.placeholder = placeholder
  return height
}
